import type { ValidationIssues } from '~/models/common'
import {
  applyDailyCheckPatch,
  buildDailyCheckRecord,
  type DailyCheckPatch,
  type DailyCheckRecord,
} from '~/models/dailyCheck'
import { toIsoTimestamp } from '~/types/dates'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

/**
 * Daily checks (D-096, pending).
 *
 * Keyed by calendar date, so a day is read and written by its own date and there
 * is structurally one record per day. A day with no record is an ordinary day
 * that was not answered — **never a day the user failed** — so `getDailyCheck`
 * returning `undefined` is a normal result and not a gap to be filled with `no`.
 */

export async function getDailyCheck(
  db: SolaDatabase,
  date: string,
): Promise<Result<DailyCheckRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.dailyChecks, date))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Every recorded day in an inclusive range.
 *
 * One bounded `IDBKeyRange` scan over the natural key, the same shape as the
 * trailing seven-day weight window. **Days with no record are simply absent from
 * the result** — the caller must not treat the gap as a run of `no` answers, and
 * `tally` in the model is written so it cannot.
 */
export async function listDailyChecksInRange(
  db: SolaDatabase,
  fromDate: string,
  toDate: string,
): Promise<Result<readonly DailyCheckRecord[], StorageError>> {
  try {
    const range = IDBKeyRange.bound(fromDate, toDate)
    const records = await db.getAll(STORE.dailyChecks, range)
    /* The natural key sorts lexically and zero-padded YYYY-MM-DD makes that
       chronological, so `getAll` already returns them in order. */
    return ok(records)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Record or amend a day's answers.
 *
 * An overwrite rather than a second record, which is what the natural date key
 * buys: correcting today's nutrition answer cannot produce two competing
 * versions of the same day.
 *
 * **This does not enforce the finalized-week boundary**, deliberately. Whether a
 * day may still be corrected depends on the weekly review that covers it, which
 * this repository cannot see without reaching into another store — and a
 * repository that read a review to decide whether to accept a write would put a
 * product rule in the wrong layer. The service that owns the correction boundary
 * calls this, and a test at that level asserts the refusal.
 */
export async function putDailyCheck(
  db: SolaDatabase,
  date: string,
  patch: DailyCheckPatch,
  now: Date,
): Promise<Result<DailyCheckRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.dailyChecks, date)
    const timestamp = toIsoTimestamp(now)

    const built =
      existing === undefined
        ? buildDailyCheckRecord(date, patch, timestamp)
        : applyDailyCheckPatch(existing, patch, timestamp)
    if (!built.ok) return err(invalid(built.error))

    await db.put(STORE.dailyChecks, built.value)
    notifyWritten(STORE.dailyChecks)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function countDailyChecks(db: SolaDatabase): Promise<Result<number, StorageError>> {
  try {
    return ok(await db.count(STORE.dailyChecks))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

function invalid(issues: ValidationIssues): StorageError {
  return storageError('invalidRecord', issues.map((entry) => entry.message).join(' '), issues)
}
